import { match } from 'ts-pattern'

import { semanticTypes } from '../../../semanticTypes'
import type { SemanticType } from '../../../semanticTypes'
import type { Condition } from '../../conditions.types'
import {
  phaseDurationFilterExpression,
  phaseNameFilterExpression
} from '../functionStrings/phase'
import { semanticTypeRuntimePath } from '../functionStrings/utils'
import { discountsInPhases, productIdsInPhases } from '../runtimeHelpers'

export const PHASE_PROPERTY = 'PHASE'
export const PHASE_SCOPED_PROPERTIES = ['PRODUCTS', 'PRODUCT'] as const
export const SCOPED_PHASES_VAR = 'scopedPhases'

export const isPhaseCondition = (condition: Condition): boolean =>
  condition.property === PHASE_PROPERTY

export const buildScopedPhasesExpression = (
  conditions: Array<Condition>
): string =>
  conditions.filter(isPhaseCondition).reduce(
    (property, condition) =>
      match(condition.operator)
        .with('NAME', () =>
          phaseNameFilterExpression({ property, value: condition.value })
        )
        .with('DURATION', () =>
          phaseDurationFilterExpression(
            {
              startDateSemanticType: semanticTypes.sequence.quote.startDate
            },
            { property, value: condition.value }
          )
        )
        .otherwise(() => '[]'),
    semanticTypeRuntimePath(semanticTypes.sequence.quote.phases)
  )

export const phaseScopedRuntimePath = ({
  propertyKey,
  semanticType
}: {
  propertyKey: string
  semanticType: SemanticType
}): string => {
  const unscopedPath = semanticTypeRuntimePath(semanticType)
  if (!isPhaseScopedProperty(propertyKey)) return unscopedPath

  return match(semanticType)
    .with(semanticTypes.sequence.quote.phases, () => SCOPED_PHASES_VAR)
    .with(semanticTypes.sequence.quote.productIds, () =>
      productIdsInPhases(SCOPED_PHASES_VAR)
    )
    .with(semanticTypes.sequence.quote.productDiscounts, () =>
      discountsInPhases(unscopedPath, SCOPED_PHASES_VAR)
    )
    .otherwise(() => unscopedPath)
}

const isPhaseScopedProperty = (propertyKey: string): boolean =>
  PHASE_SCOPED_PROPERTIES.includes(
    propertyKey as (typeof PHASE_SCOPED_PROPERTIES)[number]
  )
